import { Suspense, forwardRef, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { CreateUpdateLocationFormValues } from "../locationsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { CompanyAdminLocationsQuery, createLocationForCompany } from "../locationsApi"
import { CreateUpdateLocationForm } from "../components/CreateUpdateLocationForm"
import { COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

const Location = forwardRef<FormikProps<CreateUpdateLocationFormValues>>((props, ref) => {
    const { companyId } = useCurrentCompany()
    const navigate = useNavigate()

    const { mutateAsync: callCreateLocation } = useApiMutation(createLocationForCompany, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminLocationsQuery.LIST] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateLocationForm
                ref={ref}
                onSubmit={(data) => {
                    companyId && callCreateLocation({ location: data, companyId: companyId })
                }}
            />
        </>
    )
})

export function CreateLocation() {
    const locationFormRef = useRef<FormikProps<CreateUpdateLocationFormValues>>(null)

    const handleSaveClick = () => {
        if (locationFormRef.current) {
            locationFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="create-location-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_LOCATIONS_CREATE}
                    title="Create location"
                    rightElement={
                        <LocalizedStrict id="create-location-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-locations-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_LOCATIONS_ROUTE}>Locations</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-locations-create-title">
                            <Breadcrumb>Create location</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Location ref={locationFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
